import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';

interface StoryFeatureBlockProps {
  title: React.ReactNode;
  text: React.ReactNode;
  imageSrc: string;
  imageAlt: string;
  reversed?: boolean;
  label?: string;
}

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const BlockWrapper = styled.div<{ $reversed?: boolean }>`
  display: flex;
  flex-direction: ${({ $reversed }) => ($reversed ? 'row-reverse' : 'row')};
  align-items: center;
  gap: 3.75rem;
  margin: 0 0 5.5rem;
  width: 100%;
  animation: ${fadeIn} 0.7s ease-out;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 2rem;
    margin-bottom: 3.75rem;
  }

  @media (max-width: 480px) {
    gap: 1.75rem;
    margin-bottom: 3rem;
  }
`;

const ImageSection = styled.div`
  flex: 0 0 44%;
  min-height: 320px;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 12px 40px rgba(26, 26, 26, 0.18);
  background: linear-gradient(135deg, var(--brand-black) 0%, var(--brand-gold) 100%);
  position: relative;

  @media (max-width: 768px) {
    flex: none;
    width: 100%;
    min-height: 220px;
  }
`;

const StyledImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  position: absolute;
  top: 0;
  left: 0;
`;

const GradientPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  min-height: 320px;
  background: linear-gradient(135deg, var(--brand-black) 0%, var(--brand-gold) 100%);
  position: absolute;
  top: 0;
  left: 0;

  @media (max-width: 768px) {
    min-height: 220px;
  }
`;

const TextSection = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    align-items: center;
    text-align: center;
  }
`;

const Label = styled.span`
  display: block;
  font-family: 'Inter', sans-serif;
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: lowercase;
  color: var(--brand-gold);
  margin-bottom: 1rem;
`;

const Title = styled.h2`
  font-family: 'Space Grotesk', sans-serif;
  font-size: clamp(1.4rem, 3vw, 2rem);
  font-weight: 700;
  color: var(--brand-black);
  margin: 0 0 1.25rem;
  line-height: 1.25;
  letter-spacing: -0.02em;
  text-wrap: balance;
`;

const Body = styled.p`
  font-family: 'Inter', sans-serif;
  font-size: 1.1rem;
  line-height: 1.7;
  color: var(--brand-text-secondary);
  margin: 0;

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const StoryFeatureBlock: React.FC<StoryFeatureBlockProps> = ({
  title,
  text,
  imageSrc,
  imageAlt,
  reversed,
  label,
}) => {
  const [imgError, setImgError] = useState(false);

  return (
    <BlockWrapper $reversed={reversed}>
      <ImageSection>
        {!imgError ? (
          <StyledImage
            src={imageSrc}
            alt={imageAlt}
            loading="lazy"
            decoding="async"
            onError={() => setImgError(true)}
          />
        ) : (
          <GradientPlaceholder />
        )}
      </ImageSection>
      <TextSection>
        {label && <Label>{label}</Label>}
        <Title>{title}</Title>
        <Body>{text}</Body>
      </TextSection>
    </BlockWrapper>
  );
};

export default StoryFeatureBlock;
